import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bot, MessageSquare, Image as ImgIcon, Video, Music, Mic,
  PenLine, Code, Zap, Microscope, Briefcase, Heart,
  ArrowRight, Flame, Sparkles, Star, TrendingUp,
} from "lucide-react";
import { ToolLogo } from "@/components/ToolLogo";
import { api } from "@/lib/api";

/* ---------- Featured tools carousel — horizontal scroll ---------- */
function FeaturedCarousel() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/tools?per_page=14&page=1&sort=rank").then(r => setItems(r.data.items || []));
  }, []);

  if (!items) return <div className="skeleton h-40 rounded-2xl" />;

  return (
    <section
      className="rounded-md bg-white border border-[#E7EAF0] p-5"
      data-testid="featured-carousel"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Flame size={14} className="text-[#0B1220]" />
          <h2 className="font-display font-bold text-[16px] text-[#0B1220]">Trending Now · Ranked Frontier</h2>
        </div>
        <Link to="/?sort=rank" className="text-[11px] font-semibold text-[#5B6478] hover:text-[#0B1220] inline-flex items-center gap-1">
          Browse the ranked 500 <ArrowRight size={11} />
        </Link>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1">
        {items.map((t) => (
          <Link
            key={t.slug}
            to={`/tool/${t.slug}`}
            className="group flex-shrink-0 w-[210px] rounded-md bg-white border border-[#E7EAF0] p-3.5 hover:border-[#0B1220]/40 transition-colors"
            data-testid={`featured-${t.slug}`}
          >
            <div className="flex items-center gap-2.5 mb-2">
              <ToolLogo tool={t} size={36} rounded="lg" />
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold text-[14px] text-[#0B1220] truncate">{t.name}</div>
                <div className="text-[9.5px] uppercase tracking-wider text-[#8A93A6] truncate">{t.category}</div>
              </div>
              {t.letter_grade && (
                <span className="text-[8.5px] font-bold text-white bg-[#0B1220] px-1.5 py-0.5 rounded flex-shrink-0">{t.letter_grade}</span>
              )}
            </div>
            <p className="text-[11px] text-[#5B6478] leading-snug line-clamp-2 min-h-[28px]">{t.tagline || "—"}</p>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-[rgba(220,226,236,0.7)]">
              <div className="flex items-center gap-0.5">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} size={9} className={i <= Math.max(0, Math.min(5, Math.round(t.user_score || 0))) ? "fill-[#F97316] text-[#F97316]" : "text-[#E2E8F0] fill-[#E2E8F0]"} />
                ))}
              </div>
              <span className="text-[10px] font-mono font-bold text-[#0B1220]">{t.curio_score ?? "—"}</span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ---------- Trending categories tiles ---------- */
const CATEGORY_TILES = [
  { label: "AI Agents",     icon: Bot,          gradient: "from-teal-500/15 to-emerald-500/15", to: "/?cat=AI+Agents" },
  { label: "Chat & LLMs",   icon: MessageSquare,gradient: "from-sky-500/15 to-indigo-500/15",   to: "/?cat=Chat+%26+LLMs" },
  { label: "Image",         icon: ImgIcon,      gradient: "from-rose-500/15 to-pink-500/15",    to: "/?cat=Image" },
  { label: "Video",         icon: Video,        gradient: "from-violet-500/15 to-fuchsia-500/15",to:"/?cat=Video" },
  { label: "Music & Audio", icon: Music,        gradient: "from-amber-500/15 to-orange-500/15", to: "/?cat=Music+%26+Audio" },
  { label: "Voice",         icon: Mic,          gradient: "from-emerald-500/15 to-teal-500/15", to: "/?cat=Voice" },
  { label: "Writing",       icon: PenLine,      gradient: "from-indigo-500/15 to-purple-500/15",to: "/?cat=Writing+%26+Copy" },
  { label: "Vibe Coding",   icon: Code,         gradient: "from-cyan-500/15 to-sky-500/15",     to: "/?cat=No-Code+%26+Vibe+Coding" },
  { label: "Automation",    icon: Zap,          gradient: "from-yellow-500/15 to-amber-500/15", to: "/?cat=Automation+%26+Workflows" },
  { label: "Research",      icon: Microscope,   gradient: "from-blue-500/15 to-cyan-500/15",    to: "/?cat=Research" },
  { label: "Productivity",  icon: Briefcase,    gradient: "from-slate-500/15 to-zinc-500/15",   to: "/?cat=Productivity" },
  { label: "Health",        icon: Heart,        gradient: "from-red-500/15 to-rose-500/15",     to: "/?cat=Health+%26+Wellness" },
];

function TrendingCategories() {
  return (
    <section data-testid="trending-categories">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp size={14} className="text-[#F97316]" />
          <h2 className="font-display font-bold text-[16px] text-[#0B1220]">Trending Categories</h2>
        </div>
        <span className="text-[11px] text-[#8A93A6]">Jump into a discipline</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {CATEGORY_TILES.map((c) => {
          const Icon = c.icon;
          return (
            <Link
              key={c.label}
              to={c.to}
              className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${c.gradient} border border-white/70 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all`}
              data-testid={`cat-tile-${c.label.toLowerCase().replace(/\s+/g,'-')}`}
            >
              <div className="w-9 h-9 rounded-xl bg-white/80 backdrop-blur flex items-center justify-center mb-2 shadow-sm">
                <Icon size={16} className="text-[#0B1220]" />
              </div>
              <div className="font-display font-semibold text-[13px] text-[#0B1220]">{c.label}</div>
              <div className="text-[10px] text-[#5B6478] mt-0.5 flex items-center gap-1">
                Explore <ArrowRight size={9} className="opacity-60 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/* ---------- Stats grid ---------- */
function StatsGrid({ stats }) {
  const data = [
    { label: "AI Tools & Apps",    value: stats?.total_tools?.toLocaleString() ?? "…",           sub: "always updating" },
    { label: "Categories Covered", value: stats?.categories_covered ?? "—",                      sub: "curated taxonomies" },
    { label: "Editor Verified",    value: stats?.editor_verified?.toLocaleString() ?? "—",       sub: "scored & reviewed" },
    { label: "Frontier Models",    value: "5",                                                    sub: "GPT · Claude · Gemini · Grok · Perplexity" },
  ];
  return (
    <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="stats-grid">
      {data.map(d => (
        <div key={d.label} className="rounded-md bg-white border border-[#E7EAF0] p-4">
          <div className="font-display font-bold text-[26px] leading-none tracking-tight text-[#0B1220]">{d.value}</div>
          <div className="text-[12px] font-semibold text-[#0B1220] mt-2">{d.label}</div>
          <div className="text-[10.5px] text-[#8A93A6] mt-0.5">{d.sub}</div>
        </div>
      ))}
    </section>
  );
}

/* ---------- Popular agents spotlight ---------- */
function AgentsSpotlight() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/tools?per_page=6&page=1&cat=AI+Agents&sort=curio_score")
      .then(r => setItems(r.data.items || []));
  }, []);

  if (!items) return <div className="skeleton h-56 rounded-2xl" />;
  if (!items.length) return null;

  return (
    <section
      className="rounded-md bg-white text-[#0B1220] p-6 border border-[#E7EAF0]"
      data-testid="agents-spotlight"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[#0B1220]" />
          <h2 className="font-display font-bold text-[16px]">Popular AI Agents</h2>
        </div>
        <Link to="/agents" className="text-[11px] font-semibold text-[#5B6478] hover:text-[#0B1220] inline-flex items-center gap-1">
          See all agents <ArrowRight size={11} />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {items.map((t) => (
          <Link
            key={t.slug}
            to={`/tool/${t.slug}`}
            className="group flex items-start gap-3 rounded-md bg-white border border-[#E7EAF0] p-3.5 hover:border-[#0B1220]/40 transition-colors"
            data-testid={`agent-spot-${t.slug}`}
          >
            <ToolLogo tool={t} size={40} rounded="md" />
            <div className="min-w-0 flex-1">
              <div className="font-display font-semibold text-[13.5px] text-[#0B1220] truncate">{t.name}</div>
              <div className="text-[10px] text-[#8A93A6] uppercase tracking-wider truncate">{t.category}</div>
              <p className="text-[11px] text-[#5B6478] mt-1 leading-snug line-clamp-2">{t.tagline || "—"}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ---------- Recent Wikifi syntheses (curated static prompts for now) ---------- */
const SAMPLE_SYNTHESES = [
  { q: "Best AI for editing long-form YouTube videos in 2026?",     tag: "Video",       time: "3 min ago" },
  { q: "How do I chain Claude + Suno for a full song production?",  tag: "Music",       time: "8 min ago" },
  { q: "Free tools that rival Midjourney for photorealistic image?",tag: "Image",       time: "15 min ago" },
  { q: "Compare Cursor vs Cline vs Aider for shipping to prod",     tag: "Coding",      time: "22 min ago" },
];

function RecentSyntheses() {
  return (
    <section
      className="rounded-[22px] bg-white border border-[rgba(220,226,236,0.9)] shadow-[0_10px_40px_-24px_rgba(11,18,32,0.2)] p-5"
      data-testid="recent-syntheses"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[#F97316]" />
          <h2 className="font-display font-bold text-[16px] text-[#0B1220]">Recent Wikifi Syntheses</h2>
        </div>
        <span className="text-[10px] text-[#8A93A6] uppercase tracking-wider">Live · community</span>
      </div>
      <ul className="space-y-2">
        {SAMPLE_SYNTHESES.map((s, i) => (
          <li key={i}>
            <button
              onClick={() => {
                const inp = document.querySelector('[data-testid="wikifi-input"]');
                if (inp) { inp.value = s.q; inp.focus(); inp.dispatchEvent(new Event("input", { bubbles: true })); window.scrollTo({ top: 0, behavior: "smooth" }); }
              }}
              className="w-full text-left group flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-[#F6F7FB] transition-colors"
              data-testid={`synth-item-${i}`}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
              <span className="text-[13px] text-[#0B1220] flex-1 truncate group-hover:text-[#F97316] transition-colors">"{s.q}"</span>
              <span className="text-[10px] text-[#5B6478] bg-[#F6F7FB] rounded-full px-2 py-0.5 hidden sm:inline-block">{s.tag}</span>
              <span className="text-[10px] text-[#8A93A6] hidden md:inline-block">{s.time}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ---------- Ad Slot — subtle, labeled, non-intrusive ---------- */
function AdSlot({ variant = "banner", testid }) {
  return (
    <div
      className={`rounded-md border border-dashed border-[#E7EAF0] bg-[#FAFBFC] flex items-center justify-center ${variant === "banner" ? "h-16" : "h-40"}`}
      data-testid={testid || "ad-slot"}
    >
      <div className="text-center">
        <div className="text-[9px] uppercase tracking-[0.28em] font-bold text-[#B0B7C3]">Sponsored</div>
        <div className="text-[11px] text-[#8A93A6] mt-1">Ad Space · reach 28,655+ AI users</div>
      </div>
    </div>
  );
}

/* ---------- Combined below-window content ---------- */
export function HomeSections({ stats }) {
  return (
    <div className="mt-6 space-y-6" data-testid="home-sections">
      <FeaturedCarousel />
      <AdSlot variant="banner" testid="ad-slot-mid-banner" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2"><AgentsSpotlight /></div>
        <AdSlot variant="box" testid="ad-slot-side-box" />
      </div>
      <StatsGrid stats={stats} />
    </div>
  );
}
